import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@tanstack/react-query';

import { fetchInspectionKindList } from '#/api/imsMgmt/settings/inspectionKind/fetchList';
import { cn } from '#/lib/utils';
import { Checkbox } from '#/modules/commonComponents/checkbox';
import AppDialog from '#/modules/commonComponents/dialog/AppDialog';
import { getInspectionKindApiErrorMessage } from '#/modules/imsMgmt/settings/inspectionKind/helper/apiError';
import { mapInspectionKindRow } from '#/modules/crmMgmt/aftersales/snProductMapping/helper/inspectionKinds';
import { inspectionKindListQueryKey } from '#/utils/queryKeys';

const InspectionKindPickerDialog = ({
  open,
  onOpenChange,
  initialSelectedIds,
  blockedIds,
  onConfirm,
  isSaving = false,
}) => {
  const [keyword, setKeyword] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => new Set(initialSelectedIds ?? []));

  const listQuery = useQuery({
    queryKey: inspectionKindListQueryKey(),
    queryFn: fetchInspectionKindList,
    enabled: open,
  });

  const candidates = useMemo(() => {
    const list = listQuery.data?.data ?? [];
    const query = keyword.trim().toLowerCase();
    return list
      .map(mapInspectionKindRow)
      .filter((row) => row.status === 'ACTIVE' && row.id && !blockedIds?.has(row.id))
      .filter((row) => {
        if (!query) {
          return true;
        }
        return String(row.code).toLowerCase().includes(query)
          || String(row.name).toLowerCase().includes(query);
      });
  }, [blockedIds, keyword, listQuery.data?.data]);

  function handleToggle(id, checked) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked === true) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }

  function handleCancel() {
    if (isSaving) {
      return;
    }
    onOpenChange(false);
  }

  function handleConfirm() {
    if (isSaving) {
      return;
    }
    onConfirm(selectedIds, listQuery.data?.data ?? []);
  }

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title="選擇檢測種類"
      description="只能新增啟用中的檢測種類。已停用的既有選用可在卡片上保留或移除。"
      maxWidthClassName="sm:max-w-lg"
      closeOnOutsideClick={false}
      closeOnEscape={!isSaving}
      onCancel={handleCancel}
      onConfirm={handleConfirm}
      confirmText={isSaving ? '儲存中…' : '確定'}
      confirmDisabled={isSaving || listQuery.isLoading}
    >
      <input
        type="search"
        value={keyword}
        onChange={(event) => setKeyword(event.target.value)}
        placeholder="搜尋代號或名稱"
        className={cn(
          'mb-3 h-9 w-full rounded border border-gray-300 px-3',
          'text-sm text-gray-900',
        )}
      />
      {listQuery.isLoading ? (
        <p className="text-sm text-gray-600">載入中…</p>
      ) : null}
      {listQuery.isError ? (
        <p className="text-sm text-destructive">
          {getInspectionKindApiErrorMessage(listQuery.error, '載入檢測種類失敗')}
        </p>
      ) : null}
      {!listQuery.isLoading && !listQuery.isError && candidates.length === 0 ? (
        <p className="text-sm text-gray-600">沒有可新增的啟用檢測種類</p>
      ) : null}
      <ul className="m-0 flex max-h-80 list-none flex-col gap-2 overflow-y-auto p-0">
        {candidates.map((row) => (
          <li key={row.id}>
            <label className="flex items-center gap-2 text-sm text-gray-900">
              <Checkbox
                checked={selectedIds.has(row.id)}
                disabled={isSaving}
                onCheckedChange={(checked) => handleToggle(row.id, checked)}
                aria-label={`選取 ${row.code || row.name || '檢測種類'}`}
              />
              <span className="min-w-0 truncate">
                {row.code || '—'}
                {row.name ? ` ${row.name}` : ''}
              </span>
            </label>
          </li>
        ))}
      </ul>
    </AppDialog>
  );
};

InspectionKindPickerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  initialSelectedIds: PropTypes.arrayOf(PropTypes.string),
  blockedIds: PropTypes.instanceOf(Set),
  onConfirm: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
};

export default InspectionKindPickerDialog;
